import {
  addTabToWorkArea,
  deleteOtherTabsFromProject,
  deleteTabFromProject,
  deleteTabsFromProject,
  deleteWorkAreaFromProject,
  setActiveTabIdInWorkArea,
  setActiveWorkArea,
  useAppDispatch,
} from '@core/core/store'
import type { TabObject, WorkAreaId } from '@core/core/types'
import { Show } from '@library/components'
import type { TabType } from '@library/model'
import { getDisplayName } from '@library/model'
import cn from 'classnames'
import type { Nullable, TabId, TokenProps } from 'common/types'
import type { FC } from 'react'
import { memo, useCallback, useEffect, useState } from 'react'

import { CreateTab } from '../CreateTab'
import { getTabApp } from '../helpers'
import { Tabs } from '../Tabs'
import { TabsMenu } from '../TabsMenu'
import type { ActiveApp } from './types'
import css from './WorkArea.css'

type WorkAreaProps = TokenProps & {
  workAreaId: WorkAreaId
  tabs: TabObject[]
  activeTabId: Nullable<TabId>
  isSplitDisabled: boolean
  isWorkAreaActive: boolean
  onSplitView: () => void
}

const WorkAreaImpl: FC<WorkAreaProps> = ({
  workAreaId,
  activeTabId,
  isWorkAreaActive,
  isSplitDisabled,
  tabs,
  onSplitView,
  getToken,
}) => {
  const [tabApps, setTabApps] = useState<ActiveApp[]>([])
  const dispatch = useAppDispatch()
  const [isShowWiring, setShowWiring] = useState<boolean>(false)
  const isTabsExist = !!tabs.length
  const isActiveTab = (tabId: TabId) => activeTabId === tabId

  const activeTabType = tabs.find(({ id }) => id === activeTabId)?.type

  useEffect(() => {
    setShowWiring(activeTabId === null)
  }, [activeTabId])

  useEffect(() => {
    if (activeTabId) {
      const isHaveApp = tabApps.some(({ tabId }) => tabId === activeTabId)
      const type = tabs.find(({ id }) => id === activeTabId)?.type

      if (!isHaveApp && type) {
        const newApp = {
          tabId: activeTabId,
          app: getTabApp(type, activeTabId, getToken),
        }
        setTabApps(apps => [...apps, newApp])
      }
    }
  }, [activeTabId])

  useEffect(() => {
    if (!activeTabId && !tabs.length) {
      setTabApps([])
    }
  }, [tabs.length])

  const onShowModal = () => {
    dispatch(setActiveTabIdInWorkArea({ workAreaId, tabId: null }))
    setShowWiring(true)
  }

  const createTab = (type: TabType) => {
    dispatch(addTabToWorkArea(workAreaId, type, getDisplayName(type)))
    setShowWiring(false)
  }

  const onTabChange = (tabId: TabId) => {
    setShowWiring(false)
    dispatch(setActiveTabIdInWorkArea({ workAreaId, tabId }))
  }

  const onCloseTab = (tabId: TabId) => {
    dispatch(deleteTabFromProject(workAreaId, tabId))
    setTabApps(prev => prev.filter(tab => tab.tabId !== tabId))
  }

  const onCloseOther = (tabId: TabId) => {
    dispatch(deleteOtherTabsFromProject(workAreaId, tabId))
    setTabApps(prev => prev.filter(tab => tab.tabId === tabId))
  }

  const onDuplicate = (tabId: TabId) => {
    console.log('On duplicate', tabId)
  }

  const onPin = (tabId: TabId) => {
    console.log('on Pin', tabId)
  }

  const onPointerDownCaptureWorkArea = () => {
    if (!isWorkAreaActive) {
      dispatch(setActiveWorkArea(workAreaId))
    }
  }

  const onCloseWorkArea = useCallback(() => {
    if (isSplitDisabled) {
      dispatch(deleteWorkAreaFromProject(workAreaId))
    }
    dispatch(deleteTabsFromProject(workAreaId))
  }, [isSplitDisabled, workAreaId])

  return (
    <div className={css.container} onPointerDownCapture={onPointerDownCaptureWorkArea}>
      <div className={cn(css.tabsWrapper, { [css.filled]: isTabsExist })}>
        <Tabs
          tabs={tabs}
          isWorkAreaActive={isWorkAreaActive}
          selectedTabId={activeTabId}
          onShowModal={onShowModal}
          onTabChange={onTabChange}
          onCloseTab={onCloseTab}
          onCloseOther={onCloseOther}
          onDuplicate={onDuplicate}
          onPin={onPin}
        />

        <div className={css.menuWrapper}>
          <TabsMenu
            isSplitDisabled={isSplitDisabled}
            onSplitView={onSplitView}
            onCloseWorkArea={onCloseWorkArea}
          />
        </div>
      </div>

      <Show when={!isTabsExist || isShowWiring}>
        <CreateTab createTab={createTab} />
      </Show>

      <Show when={isTabsExist}>
        <div className={css.view}>
          {tabApps.map(({ app, tabId }) => {
            const tabAppClassName = cn(
              // TODO: После переезда геомодуля во вкладку браузера это должно исчезнуть
              tabId === activeTabId && activeTabType === 'GeoModule'
                ? css.tabAppCrossplot
                : css.tabApp,
              {
                [css.active]: isActiveTab(tabId),
              },
            )
            return (
              <div key={tabId} className={tabAppClassName}>
                {app}
              </div>
            )
          })}
        </div>
      </Show>
    </div>
  )
}

export const WorkArea = memo(WorkAreaImpl)
